import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import './App.css';
import Navigation from './components/Navigation';
import Sidebar from './components/Sidebar';
import { Toaster } from 'react-hot-toast';
import {
  Projects,
  Revisions,
  Attendence,
  Customers,
  Employees,
  Invoices,
  OnHold,
  TaskSheet,
  TaskSheetDetail,
  Users,
  Dashboard,
  AddProject,
  Permissions,
  AddEmployee,
  UserProfile,
  MyProjects,
  TeamMembers,
  BillingHistory,
  CreateInvoice,
  EditInvoice,
  ManagerOverview,
  ManageAttendance,
} from './pages';
import BillingDiagnostic from './pages/BillingDiagnostic';
import NotFound from './pages/NotFound';
import RouteMiddleware from './middleware/RouteMiddleware';
import EditProject from './pages/EditProject';
import EditEmployee from './pages/EditEmployee';
import EditUser from './pages/EditUser'; // Import the EditUser component
import Login from './pages/Login';
import { useAppSelector } from './hooks/useAppSelector';
import { useAppDispatch } from './redux/hooks';
import { fetchUsers } from './redux/actions/userActions';
import { fetchProfile } from './redux/actions/profileActions';
import { clearUserData } from './redux/slices/userSlice';
import AddCustomer from './pages/AddCustomer';
import EditCustomer from './pages/EditCustomer'; // Import the EditCustomer component
import AdminSignup from './pages/AdminSignup';
import ProtectedRoute from './components/ProtectedRoute';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.designation === 'superAdmin';
  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const mainRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  // Check screen size on load and resize
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // Auto-hide sidebar on small screens initially
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle touch events for swipe detection
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Only process swipes if we're in mobile mode
    if (isMobile) {
      // Close sidebar on left swipe
      if (isLeftSwipe && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
      // Open sidebar on right swipe
      else if (isRightSwipe && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    }

    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Add touch events to the sidebar component itself to ensure left swipe works to close it
  const onSidebarTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    // Prevent event propagation to avoid conflicts
    e.stopPropagation();
  };

  const onSidebarTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    // Prevent event propagation to avoid conflicts
    e.stopPropagation();
  };

  const onSidebarTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;

    // Only close sidebar on left swipe when it's open
    if (isLeftSwipe && isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }

    // Reset values
    setTouchStart(null);
    setTouchEnd(null);

    // Prevent event propagation to avoid conflicts
    e.stopPropagation();
  };

  // Function to close sidebar when navigation occurs
  const closeSidebarOnNavigation = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };
  return (
    <div className="min-h-screen h-full w-full max-w-[1920px] mx-auto font-inter flex bg-neutral-100 overflow-x-hidden relative">
      {/* Different sidebar behavior for desktop vs mobile */}
      {!isMobile ? (
        // Desktop behavior - toggle between full and mini sidebar
        <div
          className={`${isSidebarOpen ? 'w-[260px]' : 'w-16'} transition-all duration-300 ease-in-out flex-shrink-0 sticky top-0 h-screen`}
        >
          {/* Main Sidebar */}
          <Sidebar
            className={`${isSidebarOpen ? 'translate-x-0 w-[260px]' : '-translate-x-full invisible'} transition-transform duration-300 ease-in-out h-full`}
            variant="full"
            onNavClick={closeSidebarOnNavigation}
          />
          {/* Mini Sidebar - only visible when main sidebar is hidden */}
          {!isSidebarOpen && (
            <Sidebar
              className="w-16 translate-x-0 transition-transform duration-300 ease-in-out h-full"
              variant="mini"
              onNavClick={closeSidebarOnNavigation}
            />
          )}
        </div>
      ) : (
        // Mobile/tablet behavior with animation
        <>
          <div
            className={`fixed inset-0 bg-black z-20 transition-opacity duration-300 ${isSidebarOpen ? 'opacity-50 visible' : 'opacity-0 invisible'
              }`}
            onClick={() => setIsSidebarOpen(false)}
          />

          <div
            className="fixed z-30"
            onTouchStart={onSidebarTouchStart}
            onTouchMove={onSidebarTouchMove}
            onTouchEnd={onSidebarTouchEnd}
          >
            <Sidebar
              className={`w-[85%] sm:w-[60%] md:w-[40%] h-screen transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
              variant="full"
              onNavClick={closeSidebarOnNavigation}
            />
          </div>
        </>
      )}

      <main
        ref={mainRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={`${!isMobile ? (isSidebarOpen ? 'flex-1' : 'flex-1') : 'w-full'
          } transition-all duration-300 ease-in-out md:px-5 overflow-x-hidden`}
      >
        <Navigation
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isMobile={isMobile}
        />

        {children}
      </main>
    </div>
  );
};

export default function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector(state => state.auth);
  const { data: profileData } = useAppSelector(state => state.profile);
  const usersState = useAppSelector(state => state.users);

  // Fetch users data when the app loads and user is authenticated
  useEffect(() => {
    // Check if we've already attempted to fetch or if data is currently loading
    if (usersState.hasFetched || usersState.loading) {
      console.log('Already attempted fetch or currently loading, skipping duplicate fetch');
      return;
    }

    // Check for both admin and employee tokens
    const adminToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('pms_admin_='))
      ?.split('=')[1];

    const employeeToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('pms_employee_='))
      ?.split('=')[1];

    const token = adminToken || employeeToken;

    if (token) {
      console.log('Token found in App.tsx, dispatching fetchUsers...');

      // Set up a retry mechanism for fetching user data
      const loadUserData = (retries = 3) => {
        dispatch(fetchUsers({ forceRefresh: false }))
          .unwrap()
          .then(userData => {
            console.log('fetchUsers completed successfully, data:', userData);
          })
          .catch(error => {
            console.error('fetchUsers failed:', error);
            // Retry a few times if it fails
            if (retries > 0) {
              console.log(`Retrying fetchUsers... (${retries} retries left)`);
              setTimeout(() => loadUserData(retries - 1), 1000);
            } else {
              console.log('Max retries reached, stopping fetch attempts');
            }
          });
      };

      // Also fetch profile data if user is authenticated and profile data is not loaded
      const loadProfileData = () => {
        if (user && !profileData) {
          console.log('Token found in App.tsx, dispatching fetchProfile...');
          dispatch(fetchProfile())
            .unwrap()
            .then(profileResult => {
              console.log('fetchProfile completed successfully:', profileResult);
            })
            .catch(error => {
              console.error('fetchProfile failed:', error);
            });
        }
      };

      loadUserData();
      loadProfileData();
    } else {
      console.warn('No token found in cookies, skipping fetchUsers');
      // Clear any existing user data if no token is found
      dispatch(clearUserData());
    }
  }, [dispatch, usersState.hasFetched, usersState.loading, user, profileData]);

  // Log users state changes for debugging
  useEffect(() => {
    if (usersState?.customers?.length > 0 || usersState?.employees?.length > 0) {
      console.log('SUCCESS! Data is in Redux store:');
      console.log('Customers:', usersState.customers);
      console.log('Employees:', usersState.employees);
      console.log('Is loading:', usersState.loading);
      console.log('Error state:', usersState.error);
    } else {
      console.log('Redux users state exists but contains empty arrays:', usersState);
    }
  }, [usersState]);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#333',
          },
        }}
      />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/auth/login"
          element={
            <RouteMiddleware requireAuth={false}>
              <Login />
            </RouteMiddleware>
          }
        />

        <Route
          path="/auth/forgot-password"
          element={
            <RouteMiddleware requireAuth={false}>
              <ForgotPassword />
            </RouteMiddleware>
          }
        />

        <Route
          path="/auth/reset-password/:token"
          element={
            <RouteMiddleware requireAuth={false}>
              <ResetPassword />
            </RouteMiddleware>
          }
        />

        {/* Obscure Admin Signup Route - Keep this URL secret */}
        <Route
          path="/sys/admin/init/a7f8e9d2c4b6h3k5m8n1p9q7r2t6u4w8x1z5"
          element={
            <RouteMiddleware requireAuth={false}>
              <AdminSignup />
            </RouteMiddleware>
          }
        />

        {/* Root Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Protected Routes */}
        <Route
          element={
            <RouteMiddleware>
              <Layout>
                <Outlet />
              </Layout>
            </RouteMiddleware>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route
            path="projects"
            element={
              <ProtectedRoute permissionKey="projectRead" fallbackPath="/my-projects">
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route path="my-projects" element={<MyProjects />} />
          {/* <Route path="revisions" element={<Revisions />} /> */}
          <Route path="customers" element={<Customers />} />
          <Route path="employees" element={<Employees />} />
          <Route path="users" element={<Users />} />
          <Route path="team-members" element={<TeamMembers />} />
          <Route path="attendence" element={<Attendence />} />
          <Route path="manage-attendance" element={<ManageAttendance />} />
          <Route path="manager-overview" element={<AdminOnlyRoute><ManagerOverview /></AdminOnlyRoute>} />
          <Route path="task-sheet" element={<AdminOnlyRoute><TaskSheet /></AdminOnlyRoute>} />
          <Route path="task-sheet/employee/:employeeId" element={<AdminOnlyRoute><TaskSheetDetail /></AdminOnlyRoute>} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="billing-history" element={<BillingHistory />} />
          <Route path="billing-diagnostic" element={<BillingDiagnostic />} />
          <Route path="create-invoice" element={<CreateInvoice />} />
          <Route path="edit-invoice/:invoiceId" element={<EditInvoice />} />
          <Route path="edit-project" element={<EditProject />} />
          <Route path="edit-project/:projectId" element={<EditProject />} />
          <Route
            path="add-project"
            element={
              <ProtectedRoute permissionKey="projectWrite" fallbackPath="/projects">
                <AddProject />
              </ProtectedRoute>
            }
          />
          <Route
            path="permissions"
            element={
              <ProtectedRoute permissionKey="roleRead" fallbackPath="/dashboard">
                <Permissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="add-employee"
            element={
              <ProtectedRoute permissionKey="employeeWrite" fallbackPath="/employees">
                <AddEmployee />
              </ProtectedRoute>
            }
          />
          <Route path="profile" element={<UserProfile />} />
          <Route
            path="edit-employee/:employeeId"
            element={
              <ProtectedRoute permissionKey="employeeEdit" fallbackPath="/employees">
                <EditEmployee />
              </ProtectedRoute>
            }
          />
          <Route
            path="edit-user/:userId"
            element={
              <ProtectedRoute permissionKey="employeeEdit" fallbackPath="/users">
                <EditUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="add-customer"
            element={
              <ProtectedRoute permissionKey="customerWrite" fallbackPath="/customers">
                <AddCustomer />
              </ProtectedRoute>
            }
          />
          <Route
            path="edit-customer/:customerId"
            element={
              <ProtectedRoute permissionKey="customerEdit" fallbackPath="/customers">
                <EditCustomer />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
